// ResetPassword.js
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import OTPInput from 'react-otp-input';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Link } from 'react-router-dom';
import API from '../services/api';

const toastConfig = {
  position: 'top-center',
  autoClose: 3000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  theme: 'light',
};

const sharedStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');

  .auth-root {
    min-height: 100vh;
    background: #0a0a0f;
    display: flex; align-items: center; justify-content: center;
    font-family: 'Poppins', sans-serif;
    position: relative; overflow: hidden;
  }
  .blob { position: absolute; border-radius: 50%; filter: blur(90px); opacity: 0.18; pointer-events: none; }
  .blob-1 { width: 420px; height: 420px; background: radial-gradient(circle, #6c63ff, #a855f7); top: -80px; left: -100px; animation: drift1 10s ease-in-out infinite alternate; }
  .blob-2 { width: 340px; height: 340px; background: radial-gradient(circle, #06b6d4, #3b82f6); bottom: -60px; right: -80px; animation: drift2 12s ease-in-out infinite alternate; }
  .blob-3 { width: 200px; height: 200px; background: radial-gradient(circle, #f472b6, #ec4899); top: 50%; left: 60%; animation: drift3 8s ease-in-out infinite alternate; }
  @keyframes drift1 { from { transform: translate(0,0) scale(1); } to { transform: translate(40px,30px) scale(1.08); } }
  @keyframes drift2 { from { transform: translate(0,0) scale(1); } to { transform: translate(-30px,-40px) scale(1.05); } }
  @keyframes drift3 { from { transform: translate(0,0) scale(1); } to { transform: translate(-20px,20px) scale(0.9); } }
  .grid-overlay {
    position: absolute; inset: 0;
    background-image: linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px);
    background-size: 40px 40px; pointer-events: none;
  }
  .auth-card {
    position: relative; z-index: 10; width: 100%; max-width: 420px; margin: 24px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 24px; padding: 46px 44px;
    backdrop-filter: blur(20px);
    box-shadow: 0 0 0 1px rgba(255,255,255,0.04) inset, 0 40px 80px rgba(0,0,0,0.6), 0 0 60px rgba(108,99,255,0.06);
  }
  .auth-brand { text-align: center; margin-bottom: 28px; }
  .auth-logo {
    display: inline-flex; align-items: center; justify-content: center;
    width: 52px; height: 52px; border-radius: 14px;
    background: linear-gradient(135deg, #6c63ff, #a855f7);
    margin-bottom: 18px;
    box-shadow: 0 8px 24px rgba(108,99,255,0.35);
  }
  .auth-title {
    font-family: 'Poppins', sans-serif;
    font-size: 2rem; font-weight: 500; color: #f0f0f8;
    letter-spacing: -0.02em; line-height: 1.1; margin: 0 0 6px;
  }
  .auth-subtitle { font-size: 0.83rem; color: rgba(255,255,255,0.6); font-weight: 300; letter-spacing: 0.02em; }

  /* Step indicator */
  .step-track { display: flex; align-items: center; justify-content: center; gap: 0; margin-bottom: 30px; }
  .step-node {
    display: flex; align-items: center; justify-content: center;
    width: 28px; height: 28px; border-radius: 50%;
    font-size: 0.7rem; font-weight: 600; letter-spacing: 0.04em;
    transition: all 0.4s ease;
    position: relative; z-index: 1;
  }
  .step-node.done {
    background: linear-gradient(135deg, #6c63ff, #a855f7);
    color: white; box-shadow: 0 4px 14px rgba(108,99,255,0.4);
  }
  .step-node.active {
    background: linear-gradient(135deg, #6c63ff, #a855f7);
    color: white; box-shadow: 0 4px 14px rgba(108,99,255,0.4);
  }
  .step-node.inactive {
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.1);
    color: rgba(255,255,255,0.48);
  }
  .step-connector { width: 36px; height: 2px; transition: background 0.4s ease; }
  .step-connector.filled { background: linear-gradient(90deg, #6c63ff, #a855f7); }
  .step-connector.empty { background: rgba(255,255,255,0.08); }

  /* Divider */
  .divider { display: flex; align-items: center; gap: 12px; margin-bottom: 24px; }
  .divider-line { flex: 1; height: 1px; background: rgba(255,255,255,0.07); }
  .divider-text { font-size: 0.7rem; color: rgba(255,255,255,0.46); letter-spacing: 0.12em; text-transform: uppercase; }

  /* Fields */
  .field-group { margin-bottom: 14px; }
  .field-label { display: block; font-size: 0.7rem; font-weight: 500; letter-spacing: 0.1em; text-transform: uppercase; color: rgba(255,255,255,0.56); margin-bottom: 7px; transition: color 0.2s; }
  .field-group.focused .field-label { color: #a78bfa; }
  .field-wrapper { position: relative; }
  .field-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); width: 15px; height: 15px; color: rgba(255,255,255,0.46); transition: color 0.2s; pointer-events: none; }
  .field-group.focused .field-icon { color: #a78bfa; }
  .field-input {
    width: 100%; padding: 12px 14px 12px 40px;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 12px; color: #f0f0f8;
    font-size: 0.88rem; font-family: 'Poppins', sans-serif;
    outline: none; transition: all 0.25s ease; box-sizing: border-box;
  }
  .field-input::placeholder { color: rgba(255,255,255,0.4); }
  .field-input:focus {
    background: rgba(167,139,250,0.06);
    border-color: rgba(167,139,250,0.4);
    box-shadow: 0 0 0 3px rgba(167,139,250,0.08);
  }

  /* OTP */
  .otp-email-badge {
    display: inline-block; margin-top: 6px; padding: 4px 14px;
    background: rgba(167,139,250,0.1); border: 1px solid rgba(167,139,250,0.2);
    border-radius: 20px; font-size: 0.82rem; color: #c4b5fd; font-weight: 500;
  }
  .otp-container { display: flex !important; justify-content: center; gap: 10px; margin: 20px 0 6px; }
  .otp-input-box {
    width: 48px !important; height: 54px !important;
    background: rgba(255,255,255,0.04) !important;
    border: 1px solid rgba(255,255,255,0.1) !important;
    border-radius: 12px !important;
    color: #f0f0f8 !important; font-size: 1.3rem !important;
    font-family: 'Poppins', sans-serif !important; font-weight: 600 !important;
    text-align: center; outline: none !important;
    transition: all 0.2s ease !important;
    caret-color: #a78bfa;
  }
  .otp-input-box:focus {
    background: rgba(167,139,250,0.07) !important;
    border-color: rgba(167,139,250,0.45) !important;
    box-shadow: 0 0 0 3px rgba(167,139,250,0.1) !important;
  }

  /* Button */
  .submit-btn {
    width: 100%; padding: 14px; border-radius: 12px; border: none;
    cursor: pointer; font-family: 'Poppins', sans-serif;
    font-size: 0.9rem; font-weight: 500; letter-spacing: 0.04em; color: white;
    background: linear-gradient(135deg, #6c63ff 0%, #a855f7 100%);
    position: relative; overflow: hidden; transition: all 0.3s ease;
    box-shadow: 0 4px 20px rgba(108,99,255,0.3);
    display: flex; align-items: center; justify-content: center; gap: 8px;
    margin-top: 20px;
  }
  .submit-btn::before { content: ''; position: absolute; inset: 0; background: linear-gradient(135deg, rgba(255,255,255,0.12), transparent); opacity: 0; transition: opacity 0.3s; }
  .submit-btn:hover:not(:disabled)::before { opacity: 1; }
  .submit-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 8px 28px rgba(108,99,255,0.45); }
  .submit-btn:active:not(:disabled) { transform: translateY(0); }
  .submit-btn:disabled { opacity: 0.65; cursor: not-allowed; }
  .spinner { width: 18px; height: 18px; border: 2px solid rgba(255,255,255,0.3); border-top-color: white; border-radius: 50%; animation: spin 0.7s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* Bottom row */
  .bottom-row { text-align: center; margin-top: 22px; font-size: 0.82rem; color: rgba(255,255,255,0.52); }
  .bottom-row a { color: #a78bfa; text-decoration: none; font-weight: 500; transition: color 0.2s; }
  .bottom-row a:hover { color: #c4b5fd; }

  /* Success screen */
  .success-ring {
    display: inline-flex; align-items: center; justify-content: center;
    width: 72px; height: 72px; border-radius: 50%;
    background: rgba(167,139,250,0.1);
    border: 1.5px solid rgba(167,139,250,0.3);
    margin-bottom: 20px;
    animation: pulse-ring 2.5s ease-in-out infinite;
  }
  @keyframes pulse-ring {
    0%, 100% { box-shadow: 0 0 0 0 rgba(167,139,250,0.25); }
    50% { box-shadow: 0 0 0 12px rgba(167,139,250,0); }
  }
  .success-check { color: #a78bfa; }
  .success-desc { font-size: 0.85rem; color: rgba(255,255,255,0.6); line-height: 1.6; margin-top: 8px; }
  .login-cta {
    display: inline-flex; align-items: center; gap: 8px;
    margin-top: 28px; padding: 13px 32px;
    border-radius: 12px; font-family: 'Poppins', sans-serif;
    font-size: 0.88rem; font-weight: 500; letter-spacing: 0.04em; color: white;
    background: linear-gradient(135deg, #6c63ff, #a855f7);
    text-decoration: none; transition: all 0.3s ease;
    box-shadow: 0 4px 20px rgba(108,99,255,0.3);
  }
  .login-cta:hover { transform: translateY(-1px); box-shadow: 0 8px 28px rgba(108,99,255,0.45); }

  @media (max-width: 480px) {
    .auth-card { padding: 28px 20px; margin: 16px; }
    .auth-title { font-size: 1.7rem; }
    .otp-container { gap: 7px !important; }
    .otp-input-box { width: 38px !important; height: 46px !important; font-size: 1.1rem !important; }
  }
`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5, ease: 'easeOut', staggerChildren: 0.08 } },
  exit: { opacity: 0, transition: { duration: 0.35, ease: 'easeIn' } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
};

/* Step tracker UI */
const StepTracker = ({ current }) => {
  const steps = [1, 2, 3];
  return (
    <div className="step-track">
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <div className={`step-node ${current > s ? 'done' : current === s ? 'active' : 'inactive'}`}>
            {current > s ? (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            ) : s}
          </div>
          {i < steps.length - 1 && (
            <div className={`step-connector ${current > s ? 'filled' : 'empty'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [focusedField, setFocusedField] = useState(null);

  const currentStep = !otpSent ? 1 : !isVerified ? 2 : 3;

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await API.post(`${process.env.REACT_APP_API_URL}/api/forgot-password`, { email });
      setOtpSent(true);
      toast.success('OTP sent to your email', toastConfig);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Something went wrong', toastConfig);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match', toastConfig);
      return;
    }
    setLoading(true);
    try {
      await API.post(`${process.env.REACT_APP_API_URL}/api/reset-password`, { email, otp, newPassword });
      setIsVerified(true);
      toast.success('Password reset successfully', toastConfig);
    } catch (error) {
      console.log(error);
      toast.error(error.response?.data?.message || 'OTP verification failed', toastConfig);
    } finally {
      setLoading(false);
    }
  };

  const stepMeta = [
    { title: 'Reset password', subtitle: 'We\'ll send a code to verify it\'s you' },
    { title: 'Verify & reset', subtitle: 'Enter your code and new password' },
    { title: 'All done!', subtitle: 'Your password has been updated' },
  ];

  return (
    <>
      <style>{sharedStyles}</style>
      <motion.div className="auth-root" variants={containerVariants} initial="hidden" animate="visible" exit="exit">
        <div className="blob blob-1" /><div className="blob blob-2" /><div className="blob blob-3" />
        <div className="grid-overlay" />

        <motion.div className="auth-card" variants={itemVariants}>

          {/* Brand */}
          <motion.div className="auth-brand" variants={itemVariants}>
            <div className="auth-logo">
              {currentStep < 3 ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              )}
            </div>
            <h1 className="auth-title">{stepMeta[currentStep - 1].title}</h1>
            <p className="auth-subtitle">{stepMeta[currentStep - 1].subtitle}</p>
          </motion.div>

          {/* Step tracker */}
          <motion.div variants={itemVariants}>
            <StepTracker current={currentStep} />
          </motion.div>

          {/* ── Step 1: Email ── */}
          {!otpSent && (
            <>
              <motion.div className="divider" variants={itemVariants}>
                <div className="divider-line" /><span className="divider-text">your email</span><div className="divider-line" />
              </motion.div>

              <form onSubmit={handleRequestOtp}>
                <motion.div className={`field-group ${focusedField === 'email' ? 'focused' : ''}`} variants={itemVariants}>
                  <label className="field-label">Registered email</label>
                  <div className="field-wrapper">
                    <svg className="field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                    </svg>
                    <input
                      type="email" placeholder="you@example.com" value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onFocus={() => setFocusedField('email')} onBlur={() => setFocusedField(null)}
                      className="field-input" required
                    />
                  </div>
                </motion.div>

                <motion.button type="submit" className="submit-btn" disabled={loading} variants={itemVariants} whileTap={{ scale: 0.98 }}>
                  {loading ? (
                    <><div className="spinner" /><span>Sending code...</span></>
                  ) : (
                    <>
                      <span>Send OTP</span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                      </svg>
                    </>
                  )}
                </motion.button>

                <motion.div className="bottom-row" variants={itemVariants}>
                  Remember your password? <Link to="/login">Sign in</Link>
                </motion.div>
              </form>
            </>
          )}

          {/* ── Step 2: OTP + New Password ── */}
          {otpSent && !isVerified && (
            <>
              <motion.div style={{ textAlign: 'center', marginBottom: '20px' }} variants={itemVariants}>
                <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.6)' }}>Code sent to</p>
                <span className="otp-email-badge">{email}</span>
              </motion.div>

              <form onSubmit={handleResetPassword}>
                <motion.div variants={itemVariants}>
                  <OTPInput
                    value={otp}
                    onChange={setOtp}
                    numInputs={6}
                    renderInput={(props) => <input {...props} className="otp-input-box" />}
                    containerStyle="otp-container"
                  />
                </motion.div>

                <motion.div className="divider" style={{ marginTop: '24px' }} variants={itemVariants}>
                  <div className="divider-line" /><span className="divider-text">new password</span><div className="divider-line" />
                </motion.div>

                <motion.div className={`field-group ${focusedField === 'newpw' ? 'focused' : ''}`} variants={itemVariants}>
                  <label className="field-label">New password</label>
                  <div className="field-wrapper">
                    <svg className="field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
                    </svg>
                    <input
                      type="password" placeholder="••••••••" value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      onFocus={() => setFocusedField('newpw')} onBlur={() => setFocusedField(null)}
                      className="field-input" required
                    />
                  </div>
                </motion.div>

                <motion.div className={`field-group ${focusedField === 'confirmpw' ? 'focused' : ''}`} variants={itemVariants}>
                  <label className="field-label">Confirm new password</label>
                  <div className="field-wrapper">
                    <svg className="field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 12l2 2 4-4"/><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
                    </svg>
                    <input
                      type="password" placeholder="••••••••" value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      onFocus={() => setFocusedField('confirmpw')} onBlur={() => setFocusedField(null)}
                      className="field-input" required
                    />
                  </div>
                </motion.div>

                <motion.button type="submit" className="submit-btn" disabled={loading} variants={itemVariants} whileTap={{ scale: 0.98 }}>
                  {loading ? (
                    <><div className="spinner" /><span>Resetting...</span></>
                  ) : (
                    <>
                      <span>Reset password</span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </>
                  )}
                </motion.button>

                <motion.div className="bottom-row" variants={itemVariants}>
                  Wrong email? <span style={{ color: '#a78bfa', cursor: 'pointer', fontWeight: 500 }} onClick={() => { setOtpSent(false); setOtp(''); }}>Go back</span>
                </motion.div>
              </form>
            </>
          )}

          {/* ── Step 3: Success ── */}
          {isVerified && (
            <motion.div
              style={{ textAlign: 'center', paddingTop: '8px' }}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            >
              <div className="success-ring">
                <svg className="success-check" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <p style={{ color: '#f0f0f8', fontSize: '0.95rem', fontWeight: 500 }}>Password updated successfully</p>
              <p className="success-desc">You can now sign in with<br />your new password.</p>
              <Link to="/login" className="login-cta">
                <span>Go to sign in</span>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                </svg>
              </Link>
            </motion.div>
          )}

        </motion.div>
      </motion.div>
      <ToastContainer />
    </>
  );
};

export default ForgotPassword;